/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Normalization logic for saved views
 */

import { normalizeSavedViewsResponse } from "@/lib/utils/saved-views/normalization";
import { getPlatformPresets } from "@/lib/utils/saved-views/presets";
import type { NormalizedSavedView } from "@/types/saved-views";
import { useMemo } from "react";
import type { SavedViewType, SavedViewsResponse } from "./types";

interface UseSavedViewsNormalizationOptions<
  _TFilters extends Record<string, unknown>,
> {
  savedViewsResponse: SavedViewsResponse | undefined;
  viewType: SavedViewType;
}

/**
 * Hook for normalizing and combining saved views
 */
export function useSavedViewsNormalization<
  TFilters extends Record<string, unknown>,
>({
  savedViewsResponse,
  viewType,
}: UseSavedViewsNormalizationOptions<TFilters>) {
  // Get platform presets for view types that have them
  const platformPresets = useMemo(() => {
    if (
      viewType === "projects" ||
      viewType === "clients" ||
      viewType === "contacts" ||
      viewType === "estimates"
    ) {
      return getPlatformPresets<TFilters>(viewType);
    }
    return [];
  }, [viewType]);

  // Normalize and combine all views
  const allViews = useMemo(
    () =>
      normalizeSavedViewsResponse(
        savedViewsResponse,
        platformPresets,
      ) as NormalizedSavedView<TFilters>[],
    [savedViewsResponse, platformPresets],
  );

  return {
    allViews,
  };
}
